import { notifications } from "@mantine/notifications";
import { Query, QueryClient, useQueryClient } from "@tanstack/react-query";
import { useEffect } from "react";
import { useTranslation } from "react-i18next";

import { logger } from "#app/monitoring/Logger";
import { showErrorToast } from "#app/notifications";
import { AccessTokenSingleton } from "#modules/auth/domain/AccessTokenSingleton";
import { PART_QUERY_KEY } from "#modules/parts/infra/part.query";
import { applyRealtimeEvent } from "#shared/hooks/applyRealtimeEvent";
import { fetchEventSource } from "#shared/lib/fetchEventSource";
import { queryKeys } from "#shared/queryKeys/queryKeys";
import { JobStatusSchema, WebhookPayload } from "shared";

const HEARTBEAT_EVENT = "ping";
const STALE_CONNECTION_ERROR_THRESHOLD = 5;
const STALE_CONNECTION_NOTIFICATION_ID = "sse-stale-connection";
const EVENT_STREAM_CONTENT_TYPE = "text/event-stream";
const BASE_RETRY_INTERVAL_MS = 1000;
const MAX_RETRY_INTERVAL_MS = 30_000;
const RETRY_JITTER_MS = 1000;
const ACTIVE_JOB_STATUSES: string[] = [
  JobStatusSchema.enum.STANDBY,
  JobStatusSchema.enum.PROCESSING,
];

const ACTIVE_PART_ANALYZE_STATUS_FIELDS = [
  "analyzeStatus",
  "analyze2DStatus",
  "gltfStatus",
  "previewStatus",
  "pdfPreviewStatus",
  "embeddingStatus",
  "featuresStatus",
  "graphStatus",
  "topologicalEmbeddingStatus",
  "drawingEmbeddingStatus",
  "drawingAnalyticExtractionStatus",
  "drawingLLMExtractionStatus",
] as const;

let abortController: AbortController | null = null;
let subscriberCount = 0;
let consecutiveErrorCount = 0;
let hasWarnedStaleConnection = false;

// Bounded exponential backoff with jitter, so that browser tabs disconnected
// simultaneously (e.g. every open tab during a backend deploy) don't all retry
// in lockstep against the new instance.
const getRetryIntervalMs = (attempt: number) =>
  Math.min(BASE_RETRY_INTERVAL_MS * 2 ** attempt, MAX_RETRY_INTERVAL_MS) +
  Math.random() * RETRY_JITTER_MS;

const authenticatedFetch: typeof fetch = (input, init) => {
  const token = AccessTokenSingleton.getInstance().getAccessToken();
  const headers = new Headers(init?.headers);
  if (token) {
    headers.set("Authorization", `Bearer ${token}`);
  }
  return window.fetch(input, { ...init, headers });
};

const isQuoteRequestLineQuery = (query: Query) =>
  query.queryKey[0] === queryKeys.quoteRequestLines.all[0] && query.queryKey.length === 2;

const isPartQuery = (query: Query) =>
  query.queryKey[0] === PART_QUERY_KEY[0] && query.queryKey.length === 2;

const isPredictionQueryStillProcessing = (query: Query) => {
  const data = query.state.data as { predictionStatus?: string } | undefined;
  return !!data?.predictionStatus && ACTIVE_JOB_STATUSES.includes(data.predictionStatus);
};

const isPartAnalysisQueryStillProcessing = (query: Query) => {
  const data = query.state.data as Record<string, string | undefined> | undefined;
  if (!data) {
    return false;
  }
  return ACTIVE_PART_ANALYZE_STATUS_FIELDS.some((field) =>
    ACTIVE_JOB_STATUSES.includes(data[field] ?? ""),
  );
};

// SSE delivery over Redis pub/sub is fire-and-forget: an event published while this
// client is disconnected (network blip, tab suspended, server restart) is lost for good,
// with no replay on reconnect. Re-fetching any quoteRequestLine still awaiting a
// prediction result on every (re)connection closes that gap.
export const reconcileActivePredictionQueries = (queryClient: QueryClient) => {
  queryClient
    .getQueryCache()
    .findAll({ queryKey: queryKeys.quoteRequestLines.all })
    .filter((query) => isQuoteRequestLineQuery(query) && isPredictionQueryStillProcessing(query))
    .forEach((query) => {
      void queryClient.invalidateQueries({ queryKey: query.queryKey, exact: true });
    });
};

export const reconcileActivePartAnalysisQueries = (queryClient: QueryClient) => {
  queryClient
    .getQueryCache()
    .findAll({ queryKey: PART_QUERY_KEY })
    .filter((query) => isPartQuery(query) && isPartAnalysisQueryStillProcessing(query))
    .forEach((query) => {
      void queryClient.invalidateQueries({ queryKey: query.queryKey, exact: true });
    });
};

const startConnection = (queryClient: QueryClient, onError: () => void) => {
  if (abortController) {
    return;
  }
  abortController = new AbortController();
  void fetchEventSource(`${import.meta.env.VITE_BASE_API_URL}/events/stream`, {
    signal: abortController.signal,
    openWhenHidden: true,
    fetch: authenticatedFetch,
    onopen: async (response) => {
      const contentType = response.headers.get("content-type");
      if (!contentType?.startsWith(EVENT_STREAM_CONTENT_TYPE)) {
        throw new Error(
          `Expected content-type to be ${EVENT_STREAM_CONTENT_TYPE}, Actual: ${contentType || "null"}`,
        );
      }
      if (consecutiveErrorCount > 0 || hasWarnedStaleConnection) {
        consecutiveErrorCount = 0;
        if (hasWarnedStaleConnection) {
          hasWarnedStaleConnection = false;
          notifications.hide(STALE_CONNECTION_NOTIFICATION_ID);
        }
        void queryClient.invalidateQueries();
      } else {
        reconcileActivePredictionQueries(queryClient);
        reconcileActivePartAnalysisQueries(queryClient);
      }
    },
    onmessage: (event) => {
      if (event.event === HEARTBEAT_EVENT || !event.data) {
        return;
      }
      try {
        applyRealtimeEvent(JSON.parse(event.data) as WebhookPayload, queryClient);
      } catch (error) {
        logger.warn(`SSE: malformed payload ignored — ${String(error)}`);
      }
    },
    onclose: () => {
      // The server can end the stream cleanly (e.g. graceful shutdown during a
      // backend deploy completes the underlying Observable). Without throwing
      // here, fetchEventSource treats this as a normal termination and never
      // retries. Routing it through onerror keeps a single retry/backoff path
      // for both network errors and clean server-side closes.
      throw new Error("SSE: connection closed by server");
    },
    onerror: (error) => {
      consecutiveErrorCount += 1;
      const retryInterval = getRetryIntervalMs(consecutiveErrorCount - 1);
      logger.warn(
        `SSE: connection error, retrying in ${Math.round(retryInterval)}ms — ${String(error)}`,
      );
      if (consecutiveErrorCount >= STALE_CONNECTION_ERROR_THRESHOLD && !hasWarnedStaleConnection) {
        hasWarnedStaleConnection = true;
        onError();
      }
      return retryInterval;
    },
  });
};

const stopConnection = () => {
  abortController?.abort();
  abortController = null;
};

export const useRealtimeEvents = () => {
  const queryClient = useQueryClient();
  const { t } = useTranslation();

  useEffect(() => {
    subscriberCount += 1;
    startConnection(queryClient, () => {
      showErrorToast({
        id: STALE_CONNECTION_NOTIFICATION_ID,
        title: t("app.sseConnectionError.title"),
        message: t("app.sseConnectionError.message"),
        autoClose: false,
      });
    });

    return () => {
      subscriberCount -= 1;
      if (subscriberCount === 0) {
        stopConnection();
      }
    };
  }, [queryClient, t]);
};
